/*
 * Copyright (C) 2020-present Borza LLC. All rights reserved.
 */

import Head from "next/head";
import { Component } from "react";

import Banner from "../components/banner";
import Newsletter from "../components/newsletter";
import OpenGraphHead from "../components/og-head";
import TwitterHead from "../components/twitter-head";
import { toAbsolutePageURL } from "../utils/url";

interface IProps {
  query: {
    market: string;
  };
}

export default class People extends Component<IProps> {
  public static getInitialProps({ query }: IProps) {
    query.market = query.market ?? "en-US";

    return { query };
  }

  public render() {
    const {
      query: {
        market,
      },
    } = this.props;

    const canonicalUrl = toAbsolutePageURL("/people", market);
    const alt = "Lasting Smiles";
    const title = "People · Lasting Smiles";
    const description = "Lasting Smiles hydrating and moisturizing lip balm and lip care is designed to help you look, feel, and do your best. " +
      "Built on purpose: for people and the planet. " +
      "Our products are real, fresh, raw, and powerful. " +
      "The best self-care begins with organic and natural, plant powered ingredients sourced from nature.";

    return (
      <>
        <Head>
          <title>{title}</title>
          <meta
            content={description}
            name="description"
          />
          <link
            href={canonicalUrl}
            rel="canonical"
          />
          <link
            href={toAbsolutePageURL("/people", "en-US")}
            hrefLang="x-default"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/people", "en-US")}
            hrefLang="en-US"
            rel="alternate"
          />
          <link
            href={toAbsolutePageURL("/people", "en-CA")}
            hrefLang="en-CA"
            rel="alternate"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "Person",
                "familyName": "Mesnik-Greene",
                "givenName": "Zoe",
                "jobTitle": "Founder"
              }),
            }}
            type="application/ld+json"
          />
          <script
            dangerouslySetInnerHTML={{
              __html: JSON.stringify({
                "@context": "https://schema.org",
                "@type": "BreadcrumbList",
                "itemListElement": [
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/", market),
                    "name": "Lasting Smiles",
                    "position": 1,
                  },
                  {
                    "@type": "ListItem",
                    "item": toAbsolutePageURL("/people", market),
                    "name": "People",
                    "position": 2,
                  },
                ],
              }),
            }}
            type="application/ld+json"
          />
        </Head>
        <OpenGraphHead
          alt={alt}
          description={description}
          slug="people"
          title={title}
          type="website"
          url={canonicalUrl}
        />
        <TwitterHead
          alt={alt}
          description={description}
          slug="people"
          title={title}
        />
        <Banner />
        <Newsletter />
        <section className="px-2 py-10 sm:px-5">
          <div className="max-w-screen-lg mx-auto">
            <h1 className="font-medium font-serif text-3xl text-center">
              Lip care starts with the heart.
            </h1>
            <div className="flex flex-col-reverse sm:flex-row">
              <div className="flex-1 mr-0 sm:mr-5 sm:text-left text-center">
                <p className="my-5 text-xl">
                  Lasting Smiles is built on purpose: for people and the planet.
                  Thoughtful design.
                  Care in creation.
                  Lasting social impact.
                  <span>{" "}</span>
                  <strong>
                    Created to turn the happiness up.
                  </strong>
                </p>
                <div className="text-left">
                  <img
                    alt="Sun Icon"
                    className="inline-block relative w-24 -mb-10 z-10"
                    src={require("../public/icons/people/sun.svg") as string}
                  />
                </div>
                <div>
                  <picture>
                    <source
                      srcSet={require("../public/images/people/man-1.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/people/man-1.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Man Smiling"
                      className="inline-block my-5 rounded"
                      src={require("../public/images/people/man-1.jpg") as string}
                    />
                  </picture>
                </div>
                <br />
                <picture>
                  <source
                    srcSet={require("../public/images/people/woman-1.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/people/woman-1.jpg") as string}
                    type="image/jpeg"
                  />
                  <img
                    alt="Woman Smiling"
                    className="inline-block my-5 rounded"
                    src={require("../public/images/people/woman-1.jpg") as string}
                  />
                </picture>
                <p className="my-5 text-xl">
                  But our aspirations don't stop there.
                  <span>{" "}</span>
                  <strong>
                    We dream of a world where everyone has opportunities for greater health and well-being.
                  </strong>
                  <span>{" "}</span>
                  Each product you purchase supports that mission, so you can care for your lips while caring for people and our planet.
                  What a luscious idea.
                </p>
                <div className="text-right">
                  <img
                    alt="Water Icon"
                    className="inline-block relative w-24 -mb-16 z-10"
                    src={require("../public/icons/people/water.svg") as string}
                  />
                </div>
                <div>
                  <picture>
                    <source
                      srcSet={require("../public/images/people/woman-2.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/people/woman-2.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Woman Smiling"
                      className="inline-block my-5 rounded"
                      src={require("../public/images/people/woman-2.jpg") as string}
                    />
                  </picture>
                </div>
              </div>
              <div className="flex-1 ml-0 sm:ml-5 sm:text-left text-center">
                <picture>
                  <source
                    srcSet={require("../public/images/people/zoe-mesnik-greene.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/people/zoe-mesnik-greene.jpg") as string}
                    type="image/jpeg"
                  />
                  <img
                    alt="Zoe Mesnik-Greene, Founder"
                    className="inline-block my-5 rounded"
                    src={require("../public/images/people/zoe-mesnik-greene.jpg") as string}
                  />
                </picture>
                <p className="my-5 text-xl text-center">
                  <strong>
                    Zoe Mesnik-Greene, Founder
                  </strong>
                </p>
                <p className="my-5 text-xl">
                  We are real.
                  We are fresh.
                  We are raw.
                  We are powerful.
                  And so are our products.
                  They are designed to offer you the best self-care.
                  Radiant health begins with organic, plant-powered ingredients sourced from nature.
                  <span>{" "}</span>
                  <strong>
                    Made for you with real hearts and hands.
                  </strong>
                </p>
                <div className="text-right">
                  <img
                    alt="People Icon"
                    className="inline-block relative w-24 -mb-16 z-10"
                    src={require("../public/icons/people/people.svg") as string}
                  />
                </div>
                <div>
                  <picture>
                    <source
                      srcSet={require("../public/images/people/couple.webp") as string}
                      type="image/webp"
                    />
                    <source
                      srcSet={require("../public/images/people/couple.jpg") as string}
                      type="image/jpeg"
                    />
                    <img
                      alt="Couple Kissing"
                      className="inline-block my-5 rounded"
                      src={require("../public/images/people/couple.jpg") as string}
                    />
                  </picture>
                </div>
                <br />
                <picture>
                  <source
                    srcSet={require("../public/images/people/man-2.webp") as string}
                    type="image/webp"
                  />
                  <source
                    srcSet={require("../public/images/people/man-2.jpg") as string}
                    type="image/jpeg"
                  />
                  <img
                    alt="Man Smiling"
                    className="inline-block my-5 rounded"
                    src={require("../public/images/people/man-2.jpg") as string}
                  />
                </picture>
              </div>
            </div>
          </div>
        </section>
      </>
    );
  }
}
